/* ══════════════════════════════════════════════════════════════════════════
 * CLS-SE FAQ data (Swedish)
 * Replace with backend-driven content when /api/faq/{brandSlug} ships.
 * ══════════════════════════════════════════════════════════════════════════ */

export const CATEGORIES = [
  { id: 'ordering',  label: 'Beställning' },
  { id: 'design',    label: 'Design & tryck' },
  { id: 'delivery',  label: 'Leverans' },
  { id: 'sizing',    label: 'Storlekar & passform' },
  { id: 'payment',   label: 'Betalning' },
  { id: 'aftercare', label: 'Tvätt & skötsel' },
];

export const QUESTIONS = [
  {
    id: 'order-before-drawing',
    category: 'ordering',
    popular: true,
    popularRank: 1,
    question: 'Kan jag beställa innan vi har lämnat in teckningen?',
    answer:
      'Ja. Du kan starta beställningen och lägga din order i klasshoppen även om teckningen inte är klar. Vi sätter igång produktionen så snart vi får den slutliga teckningen från klassrepresentanten.',
  },
  {
    id: 'order-deadline',
    category: 'ordering',
    popular: true,
    popularRank: 2,
    question: 'När är sista beställningsdag?',
    answer:
      'Deadline står i din klasshop. Som tumregel måste vi ha din order senast 3 veckor före studieresan så att vi hinner med tryck, kvalitetskontroll och leverans.',
  },
  {
    id: 'modify-order',
    category: 'ordering',
    popular: true,
    popularRank: 3,
    question: 'Kan jag ändra eller avbryta min beställning?',
    answer:
      'Ja, du kan ändra din order i upp till 24 timmar efter beställning via länken "Redigera order" i din orderbekräftelse. Därefter startar produktionen och ändringar är inte längre möjliga.',
  },
  {
    id: 'no-confirmation',
    category: 'ordering',
    popular: true,
    popularRank: 4,
    question: 'Jag har inte fått min orderbekräftelse — vad gör jag?',
    answer:
      'Kontrollera först ditt skräppostfilter. Om bekräftelsen inte finns där, mejla oss på info@clseifert.com med ditt namn och skola, så skickar vi den manuellt.',
  },
  {
    id: 'drawing-adjustments',
    category: 'design',
    popular: true,
    popularRank: 5,
    question: 'Justerar ni teckningen om den inte är tryckklar?',
    answer:
      'Ja. Våra grafiker går igenom alla teckningar och gör mindre justeringar gratis så att de blir tryckklara. Vid större ändringar kontaktar vi klassrepresentanten.',
  },
  {
    id: 'sizing-help',
    category: 'sizing',
    popular: true,
    popularRank: 6,
    question: 'Hur hittar jag rätt storlek?',
    answer:
      'Vi rekommenderar att du mäter en hoodie eller t-shirt som du trivs med och jämför med vår storleksguide. Du hittar den under varje produkt.',
  },
  {
    id: 'shrinkage',
    category: 'aftercare',
    popular: true,
    popularRank: 7,
    question: 'Krymper tröjorna efter tvätt?',
    answer:
      'Våra hoodies är pre-shrunk och krymper minimalt om du tvättar dem i 30°C och hängtorkar. Tvätta alltid avig för att skydda trycket.',
  },
  {
    id: 'back-print-uniform',
    category: 'design',
    popular: true,
    popularRank: 8,
    question: 'Blir rygtrycket exakt likadant på alla tröjor?',
    answer:
      'Ja, alla rygtryck görs med samma mall, så de blir visuellt likadana. Mindre skillnader på 2–3 mm kan förekomma eftersom trycket appliceras manuellt.',
  },
  {
    id: 'delivery-time',
    category: 'delivery',
    popular: true,
    popularRank: 9,
    question: 'När får jag mina tröjor?',
    answer:
      'Standardproduktion tar 2–3 veckor från deadline. Vi skickar ett spårningsnummer så snart paketet lämnar vår verkstad.',
  },
  {
    id: 'color-variation',
    category: 'design',
    question: 'Blir trykfärgen exakt som på skärmen?',
    answer:
      'Vi matchar färgerna så nära som möjligt mot Pantone-skalan, men små skillnader mellan skärmen och fysiskt tryck kan förekomma beroende på tyg och belysning.',
  },
  {
    id: 'payment-methods',
    category: 'payment',
    question: 'Vilka betalningsmetoder kan jag använda?',
    answer:
      'Vi accepterar Visa, Mastercard, Swish och faktura. För klasser erbjuder vi även samlad faktura via skolans marknadsföringsbudget.',
  },
  {
    id: 'school-budget',
    category: 'payment',
    question: 'Kan vi betala via skolans marknadsföringsbudget?',
    answer:
      'Ja. Välj "Marknadsföringsbudget" i kassan, så skickar vi en samlad faktura till skolans administration. Beloppet dras inte från eleverna individuellt.',
  },
  {
    id: 'pay-invoice',
    category: 'payment',
    question: 'Hur betalar jag min faktura?',
    // JSX answer (FAQItem accepts nodes); searchText keeps it findable in the
    // FAQ search, which only indexes string answers.
    answer: (
      <>
        <p>
          Vi uppmuntrar dig att aldrig handla för mer än du har råd med. Betalning ska vara oss
          tillhanda senast 30 dagar efter fakturadatum. Ibland önskar man som kund betala senare än
          så men för att du inte ska lockas att handla över din budget kommer vi, för order som inte
          innehåller studentmössor, aldrig förlänga kredittiden utan du har 30 dagar på dig att betala.
        </p>
        <p>
          <strong>Delbetalning:</strong><br />
          För att delbetala fakturan kan du inte betala med knapparna på fakturan utan du gör så här:
        </p>
        <ol>
          <li>Loggar in på din bank</li>
          <li>Fyller i uppgifterna på fakturan för betalning (bankgironummer, beloppet, OCR-Nummer och vilket datum du vill betala fakturan)</li>
          <li>Du godkänner betalningen hos din bank</li>
        </ol>
        <p>
          Din betalning kommer att registreras i Billogram där du sen kommer att se hur mycket som
          återstår att betala. Du kommer inte få flera fakturor med mindre belopp utan du behåller
          din ursprungliga faktura.
        </p>
      </>
    ),
    searchText:
      'Vi uppmuntrar dig att aldrig handla för mer än du har råd med. Betalning ska vara oss tillhanda senast 30 dagar efter fakturadatum. Delbetalning bankgironummer OCR-nummer Billogram delbetala faktura kredittid',
  },
  {
    id: 'delivery-address',
    category: 'delivery',
    question: 'Måste alla tröjor levereras till skolan?',
    answer:
      'Du kan välja antingen leverans till skolan (gratis, samlat) eller hemleverans till varje elev. Hemleverans kostar 49:- per försändelse.',
  },
  {
    id: 'wrong-size',
    category: 'sizing',
    question: 'Vad gör jag om jag har beställt fel storlek?',
    answer:
      'Du kan byta till en annan storlek inom 14 dagar efter mottagandet, så länge tröjan inte är använd eller tvättad. Kontakta oss på info@clseifert.com.',
  },
];
